import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms'

export abstract class FormValidatorHelper {
    protected constructor() {}

    static validateFirstCharacter(control: AbstractControl): ValidationErrors | null {
        const value = control.value
        if (value && /^[^a-zA-Z0-9]/.test(value[0])) {
            return { invalidFirstCharacter: true }
        }
        return null
    }

    static cnpjLengthValidator(control: AbstractControl): ValidationErrors | null {
        const maskedCnpj = control.value

        if (maskedCnpj && maskedCnpj !== '') {
            const unmaskCnpj = maskedCnpj.replace(/[^\d]+/g, '')

            return unmaskCnpj.length == 14
                ? null
                : {
                      cnpjLength: { requiredValue: 14 },
                  }
        }

        return null
    }

    private static calculateCpfVerifyDigit(cpf: any, index: any) {
        const sum = cpf
            .slice(0, index)
            .split('')
            .reduce((acc: number, digit: string, idx: number) => {
                const weight = index + 1 - idx
                return acc + parseInt(digit) * weight
            }, 0)

        const remainder = sum % 11
        return remainder < 2 ? 0 : 11 - remainder
    }

    static cpfValidation(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return { cpfValidation: true }
        }

        const unmask = control.value.replace(/[.-]/g, '')

        if (!unmask) {
            return { cpfValidation: true }
        }

        if (
            unmask.length != 11 ||
            unmask == '00000000000' ||
            unmask == '11111111111' ||
            unmask == '22222222222' ||
            unmask == '33333333333' ||
            unmask == '44444444444' ||
            unmask == '55555555555' ||
            unmask == '66666666666' ||
            unmask == '77777777777' ||
            unmask == '88888888888' ||
            unmask == '99999999999'
        ) {
            return { cpfValidation: true }
        }

        const firstVerifyingDigit = FormValidatorHelper.calculateCpfVerifyDigit(unmask, 9)
        const secondVerifyingDigit = FormValidatorHelper.calculateCpfVerifyDigit(unmask, 10)

        if (parseInt(unmask.charAt(9)) !== firstVerifyingDigit || parseInt(unmask.charAt(10)) !== secondVerifyingDigit) {
            return { cpfValidation: true }
        }

        return null
    }

    static cnpjRequired(control: AbstractControl): ValidationErrors | null {
        const maskedCnpj = control.value

        if (maskedCnpj) {
            const unmaskCnpj = maskedCnpj.replace(/[^\d]+/g, '')
            return unmaskCnpj === '' ? { required: true } : null
        }

        return { required: true }
    }

    static required(control: AbstractControl): ValidationErrors | null {
        const value = control.value

        if (value === null || value === undefined || value === 0 || value === '') {
            return { required: true }
        }

        return null
    }

    static notAllowZero(control: AbstractControl): ValidationErrors | null {
        const value = control.value

        if (value === null || value === undefined || Number(value) === 0 || value === '') {
            return { required: true }
        }

        return null
    }

    static requiredAllowZero(control: AbstractControl): ValidationErrors | null {
        const value = control.value

        if (value === null || value === undefined || value === '') {
            return { required: true }
        }

        return null
    }

    static minLength(length: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value

            if (value && value.length < length) {
                return { minLength: { requiredValue: length } }
            }

            return null
        }
    }

    static maxLength(length: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value

            if (value && value.length > length) {
                return { maxLength: { requiredValue: length } }
            }

            return null
        }
    }

    static maxValue(maxValue: number, prefix = '', suffix = '') {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value

            if (value && value > maxValue) {
                return { maxValue: { value: maxValue, prefix, suffix } }
            }

            return null
        }
    }

    static minValue(minValue: number, prefix = '', suffix = ''): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value

            if (value && value < minValue) {
                return { minValue: { value: minValue, prefix, suffix } }
            }

            return null
        }
    }

    static between(minValue: number, maxValue: number) {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value

            if (value && (value > maxValue || value < minValue)) {
                return { between: { minValue, maxValue } }
            }

            return null
        }
    }

    static mustBeEqual(value: any, errorMessage: string) {
        return (control: AbstractControl): ValidationErrors | null => {
            const controlValue = control.value

            if (value && value !== controlValue) {
                return { mustBeEqual: { value, message: errorMessage } }
            }

            return null
        }
    }

    static customMinValue(value: any, errorMessage: string) {
        return (control: AbstractControl): ValidationErrors | null => {
            const controlValue = control.value

            if (value && controlValue < value) {
                return { customMinValue: { value, message: errorMessage } }
            }

            return null
        }
    }

    static customMaxValue(value: any, errorMessage: string) {
        return (control: AbstractControl): ValidationErrors | null => {
            const controlValue = control.value

            if (value && controlValue > value) {
                return { customMaxValue: { value, message: errorMessage } }
            }

            return null
        }
    }

    static email(control: AbstractControl): ValidationErrors | null {
        const value = control.value

        if (value && value.trim() !== '') {
            const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/
            const isValidFormat = emailRegex.test(value)
            const hasNoWhitespace = !/\s/.test(value)

            if (!hasNoWhitespace) return { whitespaceInEmaill: true }
            if (!isValidFormat) return { invalidEmail: true }

            if (isValidFormat && hasNoWhitespace) return null
        }

        return null
    }

    static url(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return { urlValidation: true }
        }
        const options = { onlyHttps: false }
        const isValid = control.value.startsWith('https://') || (control.value.startsWith('http://') && !options.onlyHttps)

        if (!isValid) {
            return { urlValidation: true }
        }

        let formated = control.value.replace('www.', '')
        formated = formated.replace('https://', '')
        formated = formated.replace('http://', '')

        const regex = /^[a-zA-Z0-9-_]+[:./\\]+([a-zA-Z0-9 -_./:=&"'?%+@#$!])+$/
        const returnRegex = regex.test(formated)

        return returnRegex ? null : { urlValidation: true }
    }

    static urlOnlyHttps(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return { urlValidation: true }
        }

        const regex = /^https:\/\/(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+(?:\/\S*)?$/
        const returnRegex = regex.test(control.value)

        return returnRegex ? null : { urlValidation: true }
    }

    static URLStartsWithHttps(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return { urlHttps: true }
        }
        const regex = /^https:\/\//

        return regex.test(control.value) ? null : { urlHttps: true }
    }

    static minimumPrice(minValue: number, formatterType: 'percent' | 'currency') {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value

            if (value && value < minValue) {
                return { minimumPrice: { value: minValue, formatterType } }
            }

            return null
        }
    }

    static phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
        const name = control.value as string

        if (!name) {
            return null
        }

        if (name.length < 11) {
            return { phoneNumberValidator: true }
        }

        if (name.length > 11) {
            return { phoneNumberValidator: true }
        }

        return null
    }

    static fantasyNameValidator(control: AbstractControl): ValidationErrors | null {
        const name = control.value as string

        if (!name) {
            return null
        }

        if (typeof name !== 'string') {
            return null
        }

        if (/^[^a-zA-Z0-9]/.test(name)) {
            return { invalidFirstCharacter: true }
        }

        if (name.length > 22) {
            return { fantasyNameTooLong: true }
        }

        return null
    }

    static fullNameValidator(control: AbstractControl): ValidationErrors | null {
        const name = control.value as string

        if (!name) {
            return null
        }

        if (typeof name !== 'string') {
            return null
        }

        if (name.length > 32) {
            return { fullNameTooLong: true }
        }

        return null
    }

    static withoutSpecialCharacter(control: AbstractControl): ValidationErrors | null {
        const value = control.value as string;

        if (/^[^a-zA-Z0-9]/.test(value)) {
            return { invalidFirstCharacter: true };
        }

        return null;
    }

    static documentValidator(control: AbstractControl): ValidationErrors | null {
        const documento = control.value

        if (!documento) {
            return null
        }

        const numero = documento.replace(/\D/g, '')

        if (numero.length === 11) {
            if (/^(\d)\1+$/.test(numero)) {
                return { cpfValidation: true }
            }

            const calcDigito = (base: string) =>
                base.split('').reduce((soma, num, index) => soma + parseInt(num) * (base.length + 1 - index), 0)

            const digito1 = ((calcDigito(numero.slice(0, 9)) * 10) % 11) % 10
            const digito2 = ((calcDigito(numero.slice(0, 10)) * 10) % 11) % 10

            if (!numero.endsWith(`${digito1}${digito2}`)) {
                return { cpfValidation: true }
            }

            return null
        }

        if (numero.length === 14) {
            if (/^(\d)\1+$/.test(numero)) {
                return { invalidCnpj: true }
            }

            const calcDigito = (base: string, pesos: number[]) =>
                base.split('').reduce((soma, num, index) => soma + parseInt(num) * pesos[index], 0)

            const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
            const pesos2 = [6, ...pesos1]

            const digito1 = 11 - (calcDigito(numero.slice(0, 12), pesos1) % 11)
            const digito2 = 11 - (calcDigito(numero.slice(0, 13), pesos2) % 11)

            if (!numero.endsWith(`${digito1 >= 10 ? 0 : digito1}${digito2 >= 10 ? 0 : digito2}`)) {
                return { invalidCnpj: true }
            }

            return null
        }

        return { invalidDocument: true }
    }

    static getErrorMessage(controlName: string, error: string, validatorValue?: any) {
        const currencyFormatter = new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })

        const percentFormatter = Intl.NumberFormat('pt-BR', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })

        const errorMessages = {
            phoneNumberValidator: 'O número de celular deve ter 11 caracteres',
            fullNameTooLong: 'O nome deve ter no máximo 32 caracteres',
            fantasyNameTooLong: 'O nome deve ter no máximo 22 caracteres',
            invalidFirstCharacter: 'O primeiro caractere não pode ser espaço ou caractere especial',
            cnpjLength: 'O CNPJ é obrigatório e deve ter 14 caracteres',
            invalidCnpj: 'O CNPJ infromado é inválido',
            required: `O campo ${controlName} é obrigatório`,
            maxLength: `O campo ${controlName} deve ter no máximo ${validatorValue.requiredValue} caracteres`,
            minLength: `O campo ${controlName} deve ter no mínimo ${validatorValue.requiredValue} caracteres`,
            invalidEmail: 'Informe um e-mail válido',
            whitespaceInEmaill: 'O e-mail não pode conter espaço em branco',
            urlValidation: 'Digite uma url válida, ex: https://stone.com.br',
            urlHttps: 'Digite uma url válida, ex: https://example.com',
            maxValue: `O valor máximo para o campo ${controlName}  é ${validatorValue.prefix}${validatorValue.value} ${validatorValue.suffix}`,
            minValue: `O valor mínimo para o campo ${controlName}  é ${validatorValue.prefix}${validatorValue.value} ${validatorValue.suffix}`,
            between: `O valor para campo ${controlName} precisa ser entre ${validatorValue.minValue} e ${validatorValue.maxValue}`,
            cpfValidation: 'O CPF informado é inválido',
            pricingValidation: '',
            mustBeEqual: validatorValue.message || `O campo ${controlName} deve ter o valor ${validatorValue.value}`,
            customMaxValue: validatorValue.message || `O campo ${controlName} deve ter o valor ${validatorValue.value}`,
            customMinValue: validatorValue.message || `O campo ${controlName} deve ter o valor ${validatorValue.value}`,
            minimumPrice: `O piso da oferta para campo ${controlName}  é ${
                validatorValue.formatterType === 'currency'
                    ? currencyFormatter.format(validatorValue.value)
                    : percentFormatter.format(validatorValue.value / 100)
            }`,
            invalidDocument: 'O documento informado é inválido',
        }

        // @ts-ignore
        const message = errorMessages[error]
        return message ? message : `O valor máximo para campo ${controlName}  é ${validatorValue.value}`
    }

    static mobilePhoneLength(fieldControl: FormControl) {
        if (!fieldControl.value) {
            return
        }

        const unmask = fieldControl.value.replace('(', '').replace(')', '').replace('-', '').replace(/_/g, '').replace(' ', '')

        if (unmask.length === 0) {
            return null
        }

        if (unmask.substring(2, 3) == 9) {
            return unmask.length >= 11 ? null : { phoneLength: true }
        }
        return unmask.length == 10 ? null : { phoneLength: true }
    }
}
